import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Avatar, Box, MenuItem, Paper, Popper, TextField } from '@mui/material';
import { getAtomicMentionRange, getMentionQuery, getNextMentionCandidateIndex, MentionQuery } from './mentionTextUtils';

export interface MentionCandidate {
    id: string;
    displayName: string;
    imageUrl?: string;
}

export interface MentionValue {
    userId: string;
    displayName: string;
}

interface MentionTextFieldProps {
    label: string;
    value: string;
    mentions: MentionValue[];
    candidates: MentionCandidate[];
    onChange: (value: string, mentions: MentionValue[]) => void;
    multiline?: boolean;
    rows?: number;
    size?: 'small' | 'medium';
    disabled?: boolean;
    placeholder?: string;
    required?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
}

const getCaretAnchor = (element: HTMLTextAreaElement | HTMLInputElement, caret: number): DOMRect => {
    const mirror = document.createElement('div');
    const computedStyle = window.getComputedStyle(element);
    const mirroredProperties = [
        'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
        'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
        'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
        'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize', 'fontSizeAdjust',
        'lineHeight', 'fontFamily', 'textAlign', 'textTransform', 'textIndent', 'textDecoration',
        'letterSpacing', 'wordSpacing', 'tabSize', 'whiteSpace', 'wordBreak', 'overflowWrap',
    ] as const;

    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.top = '0';
    mirror.style.left = '-9999px';
    mirror.style.whiteSpace = element instanceof HTMLTextAreaElement ? 'pre-wrap' : 'pre';
    mirror.style.wordWrap = 'break-word';
    mirroredProperties.forEach((property) => {
        mirror.style[property] = computedStyle[property];
    });

    mirror.textContent = element.value.slice(0, caret);
    const marker = document.createElement('span');
    marker.textContent = element.value.slice(caret) || '.';
    mirror.appendChild(marker);
    document.body.appendChild(mirror);

    const elementRect = element.getBoundingClientRect();
    const markerRect = marker.getBoundingClientRect();
    const x = elementRect.left + markerRect.left - mirror.getBoundingClientRect().left - element.scrollLeft;
    const y = elementRect.top + markerRect.top - mirror.getBoundingClientRect().top - element.scrollTop;
    mirror.remove();

    return new DOMRect(x, y, 1, parseFloat(computedStyle.lineHeight) || 20);
};

export const MentionTextField: React.FC<MentionTextFieldProps> = ({
    label,
    value,
    mentions,
    candidates,
    onChange,
    multiline = false,
    rows,
    size = 'small',
    disabled = false,
    placeholder,
    required = false,
    error = false,
    helperText,
    onKeyDown,
}) => {
    const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
    const pendingCaretPosition = useRef<number | null>(null);
    const [query, setQuery] = useState<MentionQuery | null>(null);
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
    const [activeCandidateIndex, setActiveCandidateIndex] = useState(0);

    const visibleCandidates = query === null
        ? []
        : candidates
            .filter((candidate) => candidate.displayName.toLocaleLowerCase().includes(query.query.toLocaleLowerCase()))
            .slice(0, 6);

    const closeSuggestions = () => {
        setQuery(null);
        setAnchorRect(null);
        setActiveCandidateIndex(0);
    };

    const updateSuggestions = (nextValue: string, caret: number, input: HTMLTextAreaElement | HTMLInputElement) => {
        const nextQuery = getMentionQuery(nextValue, caret);
        if (nextQuery === null) {
            closeSuggestions();
            return;
        }

        setQuery(nextQuery);
        setAnchorRect(getCaretAnchor(input, caret));
        setActiveCandidateIndex(0);
    };

    useLayoutEffect(() => {
        if (pendingCaretPosition.current === null || inputRef.current === null) {
            return;
        }

        inputRef.current.focus();
        inputRef.current.setSelectionRange(pendingCaretPosition.current, pendingCaretPosition.current);
        pendingCaretPosition.current = null;
    }, [value]);

    useEffect(() => closeSuggestions, []);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const nextValue = event.target.value;
        const caret = event.target.selectionStart ?? nextValue.length;
        const retainedMentions = mentions.filter((mention) => nextValue.includes(`@${mention.displayName}`));
        onChange(nextValue, retainedMentions);
        updateSuggestions(nextValue, caret, event.target);
    };

    const handleSelect = () => {
        const input = inputRef.current;
        if (input === null) {
            return;
        }
        updateSuggestions(input.value, input.selectionStart ?? input.value.length, input);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const input = event.currentTarget.querySelector('textarea, input');
        if (!(input instanceof HTMLTextAreaElement || input instanceof HTMLInputElement)) {
            onKeyDown?.(event);
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            closeSuggestions();
            return;
        }

        if (query !== null && visibleCandidates.length > 0 && event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveCandidateIndex((current) => getNextMentionCandidateIndex(current, visibleCandidates.length, 'next'));
            return;
        }

        if (query !== null && visibleCandidates.length > 0 && event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveCandidateIndex((current) => getNextMentionCandidateIndex(current, visibleCandidates.length, 'previous'));
            return;
        }

        if (event.key === 'Enter' && query !== null && visibleCandidates.length > 0) {
            event.preventDefault();
            selectCandidate(visibleCandidates[activeCandidateIndex]);
            return;
        }

        if (event.key === 'Backspace' || event.key === 'Delete') {
            const range = getAtomicMentionRange(
                input.value,
                mentions,
                input.selectionStart ?? 0,
                input.selectionEnd ?? 0,
            );
            if (range !== null) {
                event.preventDefault();
                const nextValue = `${input.value.slice(0, range.start)}${input.value.slice(range.end)}`;
                pendingCaretPosition.current = range.start;
                onChange(nextValue, mentions.filter((mention) => `@${mention.displayName}` !== input.value.slice(range.start, range.end)));
                closeSuggestions();
                return;
            }
        }

        onKeyDown?.(event);
    };

    const selectCandidate = (candidate: MentionCandidate) => {
        if (query === null || inputRef.current === null) {
            return;
        }

        const token = `@${candidate.displayName}`;
        const nextValue = `${value.slice(0, query.start)}${token} ${value.slice(query.end)}`;
        pendingCaretPosition.current = query.start + token.length + 1;
        onChange(nextValue, [...mentions.filter((mention) => mention.userId !== candidate.id), {
            userId: candidate.id,
            displayName: candidate.displayName,
        }]);
        closeSuggestions();
    };

    return (
        <>
            <TextField
                label={label}
                fullWidth
                size={size}
                multiline={multiline}
                rows={rows}
                value={value}
                onChange={handleChange}
                onSelect={() => handleSelect()}
                onClick={() => handleSelect()}
                onKeyDown={handleKeyDown}
                inputRef={inputRef}
                disabled={disabled}
                placeholder={placeholder}
                required={required}
                error={error}
                helperText={helperText}
                slotProps={{
                    htmlInput: {
                        'aria-autocomplete': 'list',
                        'aria-expanded': visibleCandidates.length > 0,
                        'aria-activedescendant': visibleCandidates.length > 0 ? `mention-option-${visibleCandidates[activeCandidateIndex].id}` : undefined,
                    },
                }}
            />
            <Popper
                open={anchorRect !== null && visibleCandidates.length > 0}
                anchorEl={anchorRect === null ? undefined : { getBoundingClientRect: () => anchorRect }}
                placement="bottom-start"
                sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
            >
                <Paper elevation={1} role="listbox" sx={{ mt: 0.5, minWidth: 240, maxWidth: 320, py: 0.5 }}>
                    {visibleCandidates.map((candidate, index) => (
                        <MenuItem
                            key={candidate.id}
                            id={`mention-option-${candidate.id}`}
                            role="option"
                            selected={index === activeCandidateIndex}
                            onMouseDown={(event) => event.preventDefault()}
                            onMouseEnter={() => setActiveCandidateIndex(index)}
                            onClick={() => selectCandidate(candidate)}
                        >
                            <Avatar sx={{ width: 28, height: 28, mr: 1 }} src={candidate.imageUrl}>
                                {candidate.displayName.charAt(0)}
                            </Avatar>
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {candidate.displayName}
                            </Box>
                        </MenuItem>
                    ))}
                </Paper>
            </Popper>
        </>
    );
};

